// Discogs-backed catalog/search/detail server functions.
// Cover art is enriched via Spotify (faster CDN + higher-res) when possible.
// Exposes: getFeaturedAlbums, getAlbumDetail, searchDiscogsPaginated,
// fetchReleaseSnapshot, DiscogsAlbum, DiscogsSearchItem.

import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@tanstack/react-router";
import { createClient } from "@supabase/supabase-js";
import {
  withSpotifyCover,
  withSpotifyCovers,
  getSpotifyAlbumDetail,
  searchSpotifyAlbums,
} from "./spotify.server";
import { cleanAlbumTitle } from "./album-title";


// Spotify album IDs are 22-char base62. Discogs release IDs are pure numeric.
// Anything else (e.g. a user_albums UUID) is an invalid catalog id.
const isDiscogsId = (id: string) => /^\d+$/.test(String(id));
const isSpotifyId = (id: string) => /^[A-Za-z0-9]{22}$/.test(String(id));

const DISCOGS_BASE = "https://api.discogs.com";
const USER_AGENT = "Viny Club/1.0 +https://viny.club";

// ---------- In-memory response cache ----------
type CacheEntry = { data: any; expires: number };
const MEM_CACHE = new Map<string, CacheEntry>();
const IN_FLIGHT = new Map<string, Promise<any>>();
const DEFAULT_TTL_MS = 1000 * 60 * 30;

async function discogs<T = any>(path: string, ttlMs = DEFAULT_TTL_MS): Promise<T> {
  const token = process.env.DISCOGS_TOKEN;
  if (!token) throw new Error("DISCOGS_TOKEN not configured");
  const sep = path.includes("?") ? "&" : "?";
  const url = path.startsWith("http") ? path : `${DISCOGS_BASE}${path}${sep}token=${token}`;
  const mem = MEM_CACHE.get(url);
  if (mem && mem.expires > Date.now()) return mem.data as T;

  const pending = IN_FLIGHT.get(url);
  if (pending) return pending as Promise<T>;

  const request = (async () => {
    let attempt = 0;
    while (true) {
      const res = await fetch(url, {
        headers: { "User-Agent": USER_AGENT, Accept: "application/json" },
        signal: AbortSignal.timeout(10_000),
      });
      if (res.status === 429 && attempt < 3) {
        const retryAfter = Number(res.headers.get("retry-after")) || 1;
        await new Promise((r) => setTimeout(r, Math.max(1000, retryAfter * 1000)));
        attempt++;
        continue;
      }
      if (res.status >= 500 && attempt < 2) {
        await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt)));
        attempt++;
        continue;
      }
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`Discogs ${res.status} on ${path}: ${body.slice(0, 200)}`);
      }
      const body = (await res.json()) as T;
      MEM_CACHE.set(url, { data: body, expires: Date.now() + ttlMs });
      return body;
    }
  })();
  IN_FLIGHT.set(url, request);
  try {
    return await request;
  } finally {
    IN_FLIGHT.delete(url);
  }
}

// ---------- Types ----------
export interface DiscogsAlbum {
  id: string;
  /** Discogs release id (numeric, as string). */
  discogsId: string;
  title: string;
  artist: string;
  year: number;
  genre: string;
  country: string;
  label: string;
  catalog: string;
  format: string;
  cover: string;
  tracklist: { pos: string; title: string; duration: string }[];
  notes?: string;
  genres?: string[];
  labels?: { name: string; catalog: string }[];
  credits?: { role: string; names: string[] }[];
  pressings?: {
    id: string;
    year: number;
    country: string;
    label: string;
    catalog: string;
    format: string;
    isCurrent: boolean;
  }[];
}

export interface DiscogsSearchItem {
  id: string;
  discogsId: string;
  title: string;
  artist: string;
  year: number;
  genre: string;
  cover: string;
}

// ---------- Helpers ----------
function cleanArtistName(name: string): string {
  // Discogs disambiguates duplicates with " (2)" — strip it.
  return name.replace(/\s*\(\d+\)\s*$/, "").trim();
}

function joinArtists(artists: Array<{ name: string; join?: string }> | undefined): string {
  if (!artists?.length) return "";
  return artists
    .map((a, i) => {
      const name = cleanArtistName(a.name || "");
      const join = i < artists.length - 1 ? ` ${(a.join || ",").trim()} ` : "";
      return `${name}${join}`;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

function parseSearchTitle(title: string): { artist: string; title: string } {
  const idx = title.indexOf(" - ");
  if (idx === -1) return { artist: "", title };
  return {
    artist: cleanArtistName(title.slice(0, idx)),
    title: title.slice(idx + 3).trim(),
  };
}

function mapSearchResult(r: any): DiscogsSearchItem {
  const parsed = parseSearchTitle(String(r.title ?? ""));
  return {
    id: String(r.id),
    discogsId: String(r.id),
    title: cleanAlbumTitle(parsed.title),
    artist: parsed.artist,
    year: Number(r.year) || 0,
    genre: Array.isArray(r.genre) ? r.genre[0] || "" : "",
    cover: String(r.cover_image || r.thumb || ""),
  };
}

function mapRelease(a: any): DiscogsAlbum {
  const cover =
    (Array.isArray(a.images) && (a.images.find((i: any) => i.type === "primary")?.uri || a.images[0]?.uri)) ||
    a.thumb ||
    "";
  const label = a.labels?.[0];
  const format = a.formats?.[0];
  const formatLabel = format
    ? [format.name, ...(format.descriptions || [])].filter(Boolean).join(" · ")
    : "";

  const labels = (a.labels ?? [])
    .map((item: any) => ({
      name: cleanArtistName(String(item.name ?? "")),
      catalog: String(item.catno ?? ""),
    }))
    .filter((item: { name: string }) => item.name);

  const creditMap = new Map<string, Set<string>>();
  const addCredits = (items: any[] | undefined) => {
    for (const item of items ?? []) {
      const name = cleanArtistName(String(item.name ?? ""));
      const role = String(item.role ?? "Participação").trim();
      if (!name) continue;
      const names = creditMap.get(role) ?? new Set<string>();
      names.add(name);
      creditMap.set(role, names);
    }
  };
  addCredits(a.extraartists);
  for (const track of a.tracklist ?? []) addCredits(track.extraartists);

  return {
    id: String(a.id),
    discogsId: String(a.id),
    title: cleanAlbumTitle(String(a.title ?? "")),
    artist: joinArtists(a.artists),
    year: Number(a.year) || 0,
    genre: Array.isArray(a.genres) ? a.genres[0] || "" : "",
    country: String(a.country ?? ""),
    label: label ? cleanArtistName(String(label.name ?? "")) : "",
    catalog: label ? String(label.catno ?? "") : "",
    format: formatLabel || "Vinyl",
    cover,
    tracklist: (a.tracklist ?? []).slice(0, 60).map((t: any) => ({
      pos: String(t.position ?? ""),
      title: String(t.title ?? ""),
      duration: String(t.duration ?? ""),
    })),
    notes: String(a.notes ?? ""),
    genres: [...(a.genres ?? []), ...(a.styles ?? [])],
    labels,
    credits: [...creditMap.entries()].map(([role, names]) => ({
      role,
      names: [...names],
    })),
  };
}

function mapPressings(release: any, versions: any[]): NonNullable<DiscogsAlbum["pressings"]> {
  const currentId = String(release.id ?? "");
  const current = {
    id: currentId,
    year: Number(release.year) || 0,
    country: String(release.country ?? ""),
    label: cleanArtistName(String(release.labels?.[0]?.name ?? "")),
    catalog: String(release.labels?.[0]?.catno ?? ""),
    format: (release.formats ?? [])
      .flatMap((item: any) => [item.name, ...(item.descriptions ?? [])])
      .filter(Boolean)
      .join(" · "),
    isCurrent: true,
  };
  const related = versions
    .filter((item) => String(item.id ?? "") !== currentId)
    .filter((item) => {
      const format = [item.format, ...(item.descriptions ?? []), ...(item.major_formats ?? [])].join(" ");
      return /vinyl/i.test(format) && !/unofficial|bootleg|test pressing/i.test(format);
    })
    .map((item) => ({
      id: String(item.id ?? ""),
      year: Number(item.released?.slice?.(0, 4) ?? item.year) || 0,
      country: String(item.country ?? ""),
      label: cleanArtistName(String(item.label ?? "")),
      catalog: String(item.catno ?? ""),
      format: [item.format, ...(item.descriptions ?? [])].filter(Boolean).join(" · "),
      isCurrent: false,
    }));
  return [current, ...related]
    .filter((item) => item.id)
    .sort((a, b) => (b.year || 0) - (a.year || 0))
    .slice(0, 24);
}

// ---------- Server functions ----------

// Featured: served from the `featured_albums_cache` table (populated by the
// /api/public/hooks/refresh-featured cron). Falls back to a live Discogs
// query if the cache is empty.
export const getFeaturedAlbums = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (url && key) {
      const supa = createClient(url, key, {
        auth: { persistSession: false, autoRefreshToken: false },
      });
      const { data } = await supa
        .from("featured_albums_cache")
        .select("discogs_id,title,artist,year,cover,genre_bucket,rank,want")
        .order("rank", { ascending: true })
        .limit(120);
      if (data && data.length) {
        return data.map((r: any) => ({
          id: String(r.discogs_id),
          discogsId: String(r.discogs_id),
          title: r.title,
          artist: r.artist,
          year: Number(r.year) || 0,
          genre: r.genre_bucket,
          country: "",
          label: "",
          catalog: "",
          format: "Vinyl",
          cover: r.cover,
          tracklist: [],
          genres: [r.genre_bucket],
        })) as DiscogsAlbum[];
      }
    }
  } catch (err) {
    console.error("getFeaturedAlbums (cache) failed", err);
  }

  // Fallback — live Discogs query if cache is cold.
  try {
    const data = await discogs<{ results: any[] }>(
      `/database/search?type=release&format=Vinyl&sort=year&sort_order=desc&per_page=50`,
      1000 * 60 * 60,
    );
    const seen = new Set<string>();
    const out: DiscogsAlbum[] = [];
    for (const r of data.results ?? []) {
      const s = mapSearchResult(r);
      if (!s.cover || seen.has(s.title + "|" + s.artist)) continue;
      seen.add(s.title + "|" + s.artist);
      out.push({
        ...s,
        country: String(r.country ?? ""),
        label: Array.isArray(r.label) ? String(r.label[0] ?? "") : "",
        catalog: String(r.catno ?? ""),
        format: Array.isArray(r.format) ? r.format.join(" · ") : "Vinyl",
        tracklist: [],
        genres: [...(r.genre ?? []), ...(r.style ?? [])],
      });
      if (out.length >= 40) break;
    }
    return await withSpotifyCovers(out);
  } catch (err) {
    console.error("getFeaturedAlbums (discogs fallback) failed", err);
    return [] as DiscogsAlbum[];
  }
});

export const getAlbumDetail = createServerFn({ method: "GET" })
  .inputValidator((input: { discogsId: string }) => input)
  .handler(async ({ data }) => {
    const id = String(data.discogsId ?? "");
    if (!id) throw notFound();
    // IDs sintéticos do scanner (MusicBrainz / Last.fm / Spotify).
    if (id.startsWith("mb:") || id.startsWith("lastfm:")) {
      const { musicDetailById } = await import("./music-lookup.server");
      const detail = await musicDetailById(id);
      if (!detail) throw notFound();
      return detail as DiscogsAlbum;
    }
    if (id.startsWith("spotify:") || isSpotifyId(id)) {
      const spotifyId = id.startsWith("spotify:") ? id.slice(8) : id;
      const sp = await getSpotifyAlbumDetail(spotifyId);
      if (!sp) throw notFound();
      return { ...sp, id, discogsId: id, notes: "" } as DiscogsAlbum;
    }
    if (!isDiscogsId(id)) throw notFound();
    const cacheKey = `album-detail:v2:${id}`;
    const { publicClient } = await import("./public-supabase.server");
    const sb = publicClient();
    const { data: cached } = await sb
      .from("discogs_cache")
      .select("data, expires_at")
      .eq("cache_key", cacheKey)
      .maybeSingle();
    const cachedAlbum = cached?.data as unknown as DiscogsAlbum | undefined;
    if (cachedAlbum && cached && new Date(cached.expires_at).getTime() > Date.now()) return cachedAlbum;

    try {
      const release: any = await discogs(`/releases/${encodeURIComponent(id)}`, 1000 * 60 * 60 * 24);
      const masterId = String(release.master_id ?? "");
      const versions = masterId
        ? await discogs<{ versions?: any[] }>(
            `/masters/${encodeURIComponent(masterId)}/versions?format=Vinyl&per_page=100`,
            1000 * 60 * 60 * 24 * 30,
          ).catch(() => ({ versions: [] }))
        : { versions: [] };
      const album: DiscogsAlbum = {
        ...mapRelease(release),
        pressings: mapPressings(release, versions.versions ?? []),
      };
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("discogs_cache").upsert({
        cache_key: cacheKey,
        data: album as never,
        expires_at: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
      });
      return album;
    } catch (error) {
      // Uma ficha já conhecida nunca desaparece por instabilidade da fonte.
      if (cachedAlbum) return cachedAlbum;
      const { data: fallback } = await sb
        .from("artist_releases")
        .select("title, year, cover_url, artists(name)")
        .eq("discogs_id", id)
        .maybeSingle();
      const relation = fallback?.artists as unknown as { name?: string } | null;
      if (fallback) {
        return {
          id,
          discogsId: id,
          title: fallback.title,
          artist: relation?.name ?? "",
          year: fallback.year ?? 0,
          genre: "",
          country: "",
          label: "",
          catalog: "",
          format: "Vinyl",
          cover: fallback.cover_url ?? "",
          tracklist: [],
          genres: [],
          pressings: [],
        } satisfies DiscogsAlbum;
      }
      console.error("getAlbumDetail failed", error);
      throw error;
    }
  });

// Hybrid catalog search — runs Spotify and Discogs in parallel and merges
// results. Spotify provides clean covers for mainstream releases; Discogs
// covers rare/classical/vintage pressings that Spotify lacks (Liszt, jazz
// reissues, prensagens regionais, etc.).
function normKey(artist: string, title: string) {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "");
  return `${norm(artist)}|${norm(title)}`;
}

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1 && !["the", "and", "de", "da", "do"].includes(t));
}

function relevance(item: { artist: string; title: string }, terms: string[]): number {
  if (!terms.length) return 0;
  const hay = tokenize(`${item.artist} ${item.title}`);
  return terms.reduce((acc, t) => acc + (hay.includes(t) ? 1 : 0), 0);
}

export const searchDiscogsPaginated = createServerFn({ method: "GET" })
  .inputValidator((input: { query: string; page?: number; perPage?: number }) => input)
  .handler(async ({ data }) => {
    const page = Math.max(1, Math.min(20, data.page ?? 1));
    const perPage = Math.max(5, Math.min(50, data.perPage ?? 20));
    const q = data.query.trim();
    if (!q) return { items: [] as DiscogsSearchItem[], page, pages: 1, hasMore: false };

    // Run Spotify and Discogs in parallel. Spotify surfaces digital/streaming
    // releases (e.g. new drops that don't have a vinyl pressing yet); Discogs
    // covers rare/vintage pressings. We no longer filter Discogs by Vinyl,
    // so reissues, CDs and digital-first albums also appear.
    async function runSearch(term: string) {
      const [spotifyRes, discogsRes] = await Promise.all([
        searchSpotifyAlbums(term, page, perPage).catch((err) => {
          console.error("searchDiscogsPaginated (spotify) failed", err);
          return { items: [] as any[], hasMore: false };
        }),
        discogs<{ results: any[]; pagination?: any }>(
          `/database/search?q=${encodeURIComponent(term)}&type=release&per_page=${perPage}&page=${page}`,
          1000 * 60 * 30,
        ).catch((err: unknown) => {
          console.error("searchDiscogsPaginated (discogs) failed", err);
          return { results: [] as any[], pagination: { pages: page } } as any;
        }),
      ]);

      const items: DiscogsSearchItem[] = [];
      const seen = new Set<string>();

      for (const s of spotifyRes.items ?? []) {
        if (!s.title || !s.artist) continue;
        const key = normKey(s.artist, s.title);
        if (seen.has(key)) continue;
        seen.add(key);
        items.push({
          id: String(s.id),
          discogsId: String(s.id),
          title: s.title,
          artist: s.artist,
          year: s.year || 0,
          genre: "",
          cover: s.cover || "",
        });
      }

      for (const r of discogsRes.results ?? []) {
        const mapped = mapSearchResult(r);
        if (!mapped.title || !mapped.artist) continue;
        const key = normKey(mapped.artist, mapped.title);
        if (seen.has(key)) continue;
        seen.add(key);
        items.push(mapped);
      }

      // Ordena por relevância ao termo buscado. O Spotify devolve muito ruído
      // em buscas com "artista + álbum" (ex.: "Nonagon Infinity King Gizzard"),
      // e antes essas sobras ocupavam a página inteira antes dos resultados
      // certos do Discogs.
      const terms = tokenize(term);
      const scored = items
        .map((it) => ({ it, score: relevance(it, terms) }))
        .sort((a, b) => b.score - a.score);
      const best = scored[0]?.score ?? 0;
      // Se existe correspondência forte, descarta o ruído sem nenhum termo em comum.
      const kept = best >= terms.length ? scored.filter((s) => s.score > 0) : scored;

      return { items: kept.map((s) => s.it), spotifyRes, discogsRes };
    }


    let result = await runSearch(q);
    let usedQuery = q;
    let correctedFrom: string | null = null;

    // Tolerância a erros de digitação: se nada (ou quase nada) foi achado,
    // pede uma correção fuzzy do nome do artista e repete a busca.
    if (result.items.length < 3) {
      const { suggestQueryCorrection } = await import("./music-lookup.server");
      const corrected = await suggestQueryCorrection(q).catch(() => null);
      if (corrected) {
        const retry = await runSearch(corrected);
        if (retry.items.length > result.items.length) {
          result = retry;
          usedQuery = corrected;
          correctedFrom = q;
        }
      }
    }

    // Mesmo motor do scanner (MusicBrainz + Last.fm) aplicado à busca textual:
    // álbuns que o Spotify/Discogs não devolvem (ex.: "King Gizzard & the
    // Lizard Wizard - Nonagon Infinity") passam a aparecer aqui também.
    if (page === 1 && result.items.length < perPage) {
      try {
        const { mbCandidates, lastfmSearch } = await import("./music-lookup.server");
        const [mb, lf] = await Promise.all([
          mbCandidates(null, null, usedQuery).catch(() => []),
          lastfmSearch(usedQuery).catch(() => []),
        ]);
        const seen = new Set(result.items.map((it) => normKey(it.artist, it.title)));
        const terms = tokenize(usedQuery);
        const extras: DiscogsSearchItem[] = [];
        for (const m of [...mb, ...lf]) {
          if (!m.artist || !m.album) continue;
          const key = normKey(m.artist, m.album);
          if (seen.has(key)) continue;
          if (terms.length && relevance({ artist: m.artist, title: m.album }, terms) === 0) continue;
          seen.add(key);
          extras.push({
            id: m.id,
            discogsId: m.id,
            title: m.album,
            artist: m.artist,
            year: m.year || 0,
            genre: "",
            cover: m.cover || "",
          });
        }
        if (extras.length) {
          const merged = [...result.items, ...extras]
            .map((it) => ({ it, score: relevance(it, terms) }))
            .sort((a, b) => b.score - a.score)
            .map((s) => s.it)
            .slice(0, perPage);
          result = { ...result, items: merged };
        }
      } catch (err) {
        console.error("searchDiscogsPaginated (music fallback) failed", err);
      }
    }



    // Fill missing covers from Spotify.
    const enriched = await withSpotifyCovers(result.items).catch(() => result.items);

    const discogsPages = Number(result.discogsRes.pagination?.pages) || page;
    const hasMore = discogsPages > page || Boolean(result.spotifyRes.hasMore);

    return {
      items: enriched,
      page,
      pages: discogsPages,
      hasMore,
      query: usedQuery,
      correctedFrom,
    };
  });



// Discogs-only search — vinyl-only. Never surface CD/cassette/digital versions.
export const searchDiscogsOnly = createServerFn({ method: "GET" })
  .inputValidator((input: { query: string; page?: number; perPage?: number }) => input)
  .handler(async ({ data }) => {
    const page = Math.max(1, Math.min(20, data.page ?? 1));
    const perPage = Math.max(5, Math.min(50, data.perPage ?? 20));
    const q = data.query.trim();
    if (!q) return { items: [] as DiscogsSearchItem[], page, pages: 1, hasMore: false };

    const res = await discogs<{ results: any[]; pagination?: any }>(
      `/database/search?q=${encodeURIComponent(q)}&type=release&format=Vinyl&per_page=${perPage}&page=${page}`,
      1000 * 60 * 30,
    ).catch((err: unknown) => {
      console.error("searchDiscogsOnly failed", err);
      return { results: [] as any[], pagination: { pages: page } } as any;
    });

    const items: DiscogsSearchItem[] = [];
    const seen = new Set<string>();
    for (const r of res.results ?? []) {
      const mapped = mapSearchResult(r);
      if (!mapped.title) continue;
      const key = `${normKey(mapped.artist, mapped.title)}|${mapped.year}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push(mapped);
    }

    const pages = Number(res.pagination?.pages) || page;
    return { items, page, pages, hasMore: pages > page };
  });

// Used by collection/wishlist server fns to snapshot album metadata at add-time.
export async function fetchReleaseSnapshot(discogsId: string) {
  if (!discogsId) throw new Error("missing albumId");
  if (isSpotifyId(discogsId)) {
    const sp = await getSpotifyAlbumDetail(discogsId);
    if (!sp) throw new Error("album not found");
    return {
      discogsId: sp.id,
      title: sp.title,
      artist: sp.artist,
      year: sp.year || null,
      cover: sp.cover,
      genres: sp.genres,
    };
  }
  const release = await discogs(`/releases/${encodeURIComponent(discogsId)}`);
  const discogsCover =
    (Array.isArray(release.images) &&
      (release.images.find((i: any) => i.type === "primary")?.uri || release.images[0]?.uri)) ||
    "";
  const artist = joinArtists(release.artists);
  const title = cleanAlbumTitle(String(release.title || ""));
  const enriched = await withSpotifyCover({ artist, title, cover: discogsCover });
  return {
    discogsId: String(release.id),
    title,
    artist,
    year: Number(release.year) || null,
    cover: enriched.cover,
    genres: [...(release.genres ?? []), ...(release.styles ?? [])],
  };
}
